<template>
    <div
        ref="app"
        id="cz3"
        :class="[
            'cz3',
            {
                'cz3--loaded': loaded,
                'cz3--hide-outlines': hideOutlines,
            },
            `cz3--locale-${cleanedLocale}`,
            `cz3--short-locale-${shortCleanedLocale}`,
        ]"
        @keydown="keyDown"
        @mousedown="mouseDown"
    >
        <customizer-main v-if="loaded"></customizer-main>

        <transition name="cz3__fade">
            <div
                v-if="globalBusy || !loaded"
                class="absolute inset-0 bg-white/80 z-[50] flex items-center justify-center"
            >
                <loading-spinner v-if="!globalQuietBusy" class="w-12 h-12 md:w-20 md:h-20 text-ink" />
            </div>
        </transition>
    </div>
</template>

<style>
    #cz3 {
        /* Highest possible z-index to ensure our content covers the client's content */
        /* Not that high... */
        z-index: 2000000;
        position: absolute;
        top: 0;
        width: 100vw;
        height: 100%;
        overflow: hidden;
    }

    html.cz3__overlay-frame,
    html.cz3__overlay-frame > body {
        height: 100vh;
        height: var(--app-height);
        overflow: hidden;
    }

    .cz3__overlay-lock > *:not(.cz3):not(#tasks-container) {
        display: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }
</style>

<script>
    // eslint-disable-next-line
    import URL from 'url-polyfill';
    // eslint-disable-next-line
    import '@netless/canvas-polyfill';

    import allStores from './stores/all';

    import CustomizerMain from './components/CustomizerMain.vue';
    import LoadingSpinner from './components/LoadingSpinner.vue';

    export default {
        mixins: [allStores],

        components: {
            CustomizerMain,
            LoadingSpinner,
        },

        props: {
            options: Object,
        },

        data() {
            return {
                // Is the main UI loaded.
                loaded: true,

                // Hide outlines when using only the mouse.
                hideOutlines: true,
            };
        },

        computed: {
            /**
             * Is the UX blocked?
             */
            globalBusy() {
                return this.appStore.isBusy;
            },

            /**
             * Is the UX blocked?
             */
            globalQuietBusy() {
                return this.appStore.isQuietBusy;
            },

            /**
             * Shortcut to self-destruct flag.
             */
            selfDestruct() {
                return this.appStore.selfDestruct;
            },

            /**
             * Watch locale.
             */
            locale() {
                return this.customizerStore.options?.locale;
            },

            /**
             * Get normalized locale ID.
             */
            cleanedLocale() {
                if (this.locale) {
                    return this.locale.replace('_', '-').toLowerCase();
                }

                return 'generic';
            },

            /**
             * Get short normalized locale ID (i.e. for en-US return en only).
             */
            shortCleanedLocale() {
                const locale = this.cleanedLocale;

                return locale.split('-')[0];
            },

            /**
             * Main controller.
             */
            viewController() {
                return this.customizerStore.viewController;
            },
        },

        watch: {
            viewController(newValue) {
                if (newValue) {
                    this.loaded = true;
                }
            },

            locale() {
                if (this.locale) {
                    this.$i18n.locale = this.locale;
                }
            },

            /**
             * Begin self-destruct sequence.
             */
            selfDestruct(v) {
                if (v) {
                    this.loaded = false;

                    this.$nextTick(() => {
                        this.$emit('selfDestruct');
                    });
                }
            },
        },

        beforeMount() {
            if (this.options != null) {
                if (this.options.region == null) {
                    if (this.options.marketingUnit != null) {
                        // eslint-disable-next-line vue/no-mutating-props
                        this.options.region = this.options.marketingUnit;
                    }
                }

                if (this.options.region == null) {
                    if (this.options.locale != null) {
                        const localeParts = this.options.locale.replace('_', '-').split('-');
                        if (localeParts[1] === 'US') {
                            // eslint-disable-next-line vue/no-mutating-props
                            this.options.region = 'US';
                        }
                        if (localeParts[1] === 'CA') {
                            // eslint-disable-next-line vue/no-mutating-props
                            this.options.region = 'CA';
                        }
                    }
                }
            }
            // Save options in all modules.
            this.appStore.setOptions(this.options);

            this.appStore.setTranslations(this.$root.$i18n);

            this.customizerStore.setOptions(this.options);
        },

        mounted() {
            this.configureSizing();

            // Once the popup is in DOM, load the product data.
            this.customizerStore.load({
                options: this.options,
            });
        },

        beforeUnmount() {
            this.stopResizing();
        },

        methods: {
            /**
             * Configure the resize of the widget so it always takes the full height.
             */
            configureSizing() {
                // Reset scroll position.
                this.oldBodyScroll = document.body.scrollTop || document.documentElement.scrollTop;

                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;

                document.body.style.height = this.lockHeight ? `${this.lockHeight}px` : `${this.$refs.app.offsetHeight}px`;

                // Hide everything so we have a clean page.
                document.body.classList.add('cz3__overlay-lock');
                document.documentElement.classList.add('cz3__overlay-frame');

                // Listen to resize.
                window.addEventListener('resize', this.resize);

                // And resize right away.
                this.$nextTick(() => {
                    this.resize();
                });

                // Because the scrolling state may have changed now with the body height adjusted, reevaluate sizing a moment later.
                this.heightTimer = setInterval(this.resize, 100);
            },

            /**
             * Cleanly destroy the widget.
             */
            stopResizing() {
                document.body.classList.remove('cz3__overlay-lock');
                document.documentElement.classList.remove('cz3__overlay-frame');
                document.body.style.height = 'auto';

                window.removeEventListener('resize', this.resize);

                if (this.oldBodyScroll) {
                    document.body.scrollTop = this.oldBodyScroll;
                    document.documentElement.scrollTop = this.oldBodyScroll;
                }

                if (this.heightTimer) {
                    clearInterval(this.heightTimer);
                }
            },

            /**
             * Update sizing parameters.
             */
            resize() {
                const h = window.innerHeight;

                if (this.lastHeight !== h) {
                    // Resize the frame.
                    this.$refs.app.style.setProperty('--app-height', `${h}px`);

                    // Adjust the body height for the full screen effect.
                    document.body.style.height = `${this.$refs.app.offsetHeight}px`;

                    this.lastHeight = h;
                }

                this.checkIfMobile();
            },

            /**
             * See if the screen is below the lg breakpoint.
             */
            checkIfMobile() {
                this.appStore.setIsMobile(window.innerWidth < 1024);
            },

            /**
             * Hides outlines on mouse actions.
             */
            mouseDown() {
                this.hideOutlines = true;
            },

            /**
             * Shows outlines again when keyboard is engaged.
             */
            keyDown() {
                this.hideOutlines = false;
            },
        },
    };
</script>
